import React, { useEffect, useState } from 'react';
import { can } from '../permissions';
import api from '../api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n||0).toLocaleString();
const fmtTime = (m) => { if(!m) return '—'; const h=Math.floor(m/60); const mn=m%60; return h>0?`${h}h ${mn}m`:`${mn}m`; };

export default function DriverReports() {
  const today = new Date().toISOString().split('T')[0];
  const firstDay = new Date(new Date().setDate(1)).toISOString().split('T')[0];
  const [drivers, setDrivers] = useState([]);
  const [selDriver, setSelDriver] = useState('');
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [newStatus, setNewStatus] = useState('cancelled');
  const [statusReason, setStatusReason] = useState('');
  const [statusDate, setStatusDate] = useState('');

  useEffect(() => {
    api.get('/drivers').then(r => { setDrivers(r.data); if (r.data.length) setSelDriver(r.data[0].id); });
  }, []);

  const loadReport = () => {
    if (!selDriver) return;
    setLoading(true);
    api.get(`/analytics/driver-report/${selDriver}?from=${from}&to=${to}`)
      .then(r => setReport(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReport();
  }, [selDriver, from, to]);

  const submitStatusChange = async () => {
    if (newStatus === 'rescheduled' && !statusDate) { toast.error('Pick a reschedule date'); return; }
    try {
      await api.post('/deliveries/admin-change-status/' + statusModal.id, {
        new_status: newStatus,
        reason: statusReason || undefined,
        reschedule_date: newStatus === 'rescheduled' ? statusDate : undefined,
      });
      toast.success('Status updated');
      setStatusModal(null); setStatusReason(''); setStatusDate('');
      loadReport();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to change status'); }
  };

  const openStatusModal = (inv) => {
    setStatusModal(inv);
    setNewStatus('cancelled');
    setStatusReason('');
    setStatusDate('');
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this entire session and all its invoices? This cannot be undone.')) return;
    try {
      await api.delete('/deliveries/session/' + sessionId);
      toast.success('Session deleted');
      loadReport();
    } catch { toast.error('Error deleting session'); }
  };

  const deleteInvoice = async (invoiceId, sessionId) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    try {
      await api.delete('/invoices/' + invoiceId);
      toast.success('Invoice deleted');
      setReport(prev => ({
        ...prev,
        sessions: prev.sessions.map(s =>
          s.id === sessionId ? { ...s, invoices: s.invoices.filter(i => i.id !== invoiceId) } : s
        )
      }));
    } catch { toast.error('Error deleting invoice'); }
  };

  const t = report?.totals || {};
  const presets = [
    { label: 'Today', from: today, to: today },
    { label: 'This week', from: new Date(new Date().setDate(new Date().getDate()-7)).toISOString().split('T')[0], to: today },
    { label: 'This month', from: firstDay, to: today },
    { label: 'Last 3 months', from: new Date(new Date().setMonth(new Date().getMonth()-3)).toISOString().split('T')[0], to: today },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Driver Reports</h1>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>Detailed performance report for management — every stop, photo, and odometer reading</p>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={selDriver} onChange={e => setSelDriver(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
          {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          {presets.map(p => (
            <button key={p.label} onClick={() => { setFrom(p.from); setTo(p.to); }}
              style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid #e2e8f0', fontSize: 12, cursor: 'pointer',
                background: from === p.from && to === p.to ? '#0284c7' : '#f8fafc',
                color: from === p.from && to === p.to ? '#fff' : '#475569' }}>
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading report...</div>}

      {!loading && report && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 12, marginBottom: 24 }}>
            {[
              ['📅 Days worked', t.total_days, '#0284c7'],
              ['📦 Total stops', t.total_stops, '#7c3aed'],
              ['✅ Delivered', t.delivered, '#16a34a'],
              ['❌ Cancelled', t.cancelled, '#dc2626'],
              ['📅 Rescheduled', t.rescheduled, '#a78bfa'],
              ['🛣️ KM (GPS)', Number(t.total_km||0).toFixed(1), '#f59e0b'],
              ['🚗 KM (Odometer)', t.total_odo_km || '—', '#0891b2'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ background: '#0f172a', borderRadius: 12, padding: 18, marginBottom: 20, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{report.driver?.name} — Total Value Collected</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#4ade80' }}>AED {fmt(t.total_value)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Total time on routes</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtTime(t.total_minutes)}</div>
            </div>
          </div>

          {/* Sessions list */}
          {report.sessions.map(s => {
            const isExpanded = expandedSession === s.id;
            const eff = s.invoices.length ? Math.round((s.invoices.filter(i => i.status === 'delivered').length / s.invoices.length) * 100) : 0;
            return (
              <div key={s.id} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12, overflow: 'hidden' }}>
                <div onClick={() => setExpandedSession(isExpanded ? null : s.id)}
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{new Date(s.session_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {s.started_at ? new Date(s.started_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'} → {s.completed_at ? new Date(s.completed_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}
                      {' '}· {fmtTime(s.actual_duration)} · {Number(s.actual_km||0).toFixed(1)} km
                      {s.odo_start && s.odo_end && ` · Odo: ${s.odo_start}→${s.odo_end} (${s.odo_end-s.odo_start} km)`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{s.invoices.filter(i=>i.status==='delivered').length}/{s.invoices.length} delivered</div>
                      <div style={{ fontSize: 11, color: eff>=80?'#16a34a':eff>=50?'#f59e0b':'#dc2626' }}>{eff}% success</div>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: 18 }}>{isExpanded ? '▲' : '▼'}</span>
                  {can('driver_reports','delete') && <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    style={{ padding: '4px 10px', background: '#fef2f2', border: 'none', borderRadius: 6, color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginLeft: 8 }}>
                    🗑 Delete
                  </button>}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px', background: '#fafbfc' }}>
                    {/* Odometer photos */}
                    {(s.odo_start_photo || s.odo_end_photo) && (
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        {s.odo_start_photo && (
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Odo Start: {s.odo_start} km</div>
                            <img src={s.odo_start_photo} alt="odo start" onClick={() => setLightbox(s.odo_start_photo)} style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid #e2e8f0' }} />
                          </div>
                        )}
                        {s.odo_end_photo && (
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Odo End: {s.odo_end} km</div>
                            <img src={s.odo_end_photo} alt="odo end" onClick={() => setLightbox(s.odo_end_photo)} style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid #e2e8f0' }} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stop by stop */}
                    {s.invoices.sort((a,b)=>(a.stop_order||999)-(b.stop_order||999)).map((inv, i) => (
                      <div key={inv.id} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: i < s.invoices.length-1 ? '1px solid #f1f5f9' : 'none' }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          background: inv.status==='delivered'?'#16a34a':inv.delivery_status==='cancelled'?'#dc2626':inv.delivery_status==='rescheduled'?'#7c3aed':'#94a3b8',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700
                        }}>{inv.stop_order || i+1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{inv.customer_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{inv.customer_code} · {inv.salesman_name} · AED {fmt(inv.invoice_value)}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            {inv.reached_at && `Reached: ${new Date(inv.reached_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`}
                            {inv.delivered_at && ` · Done: ${new Date(inv.delivered_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`}
                          </div>
                          {(inv.travel_time != null || inv.service_time != null) && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                              {inv.travel_time != null && (
                                <span style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>
                                  🚗 Travel: {inv.travel_time}m
                                </span>
                              )}
                              {inv.service_time != null && (
                                <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>
                                  🏪 Shop: {inv.service_time}m
                                </span>
                              )}
                            </div>
                          )}
                          {inv.location_distance != null && (
                            <div style={{ marginTop: 3 }}>
                              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, fontWeight: 600,
                                background: inv.location_distance <= 100 ? '#dcfce7' : inv.location_distance <= 500 ? '#fef3c7' : '#fee2e2',
                                color: inv.location_distance <= 100 ? '#16a34a' : inv.location_distance <= 500 ? '#b45309' : '#dc2626' }}
                                title="Distance between customer's expected location and where the driver took the shop photo">
                                📍 {inv.location_distance < 1000 ? inv.location_distance + 'm' : (inv.location_distance/1000).toFixed(1) + 'km'} from set location
                              </span>
                              {inv.actual_latitude && (
                                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
                                  Expected: {Number(inv.latitude).toFixed(5)}, {Number(inv.longitude).toFixed(5)} · Actual: {Number(inv.actual_latitude).toFixed(5)}, {Number(inv.actual_longitude).toFixed(5)}
                                </div>
                              )}
                            </div>
                          )}
                          {inv.cancel_reason && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Reason: {inv.cancel_reason}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-start' }}>
                          {inv.shop_photo && (
                            <img src={inv.shop_photo} alt="shop" onClick={() => setLightbox(inv.shop_photo)} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #e2e8f0' }} title="Shop photo" />
                          )}
                          {inv.invoice_photo && (
                            <img src={inv.invoice_photo} alt="invoice" onClick={() => setLightbox(inv.invoice_photo)} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #e2e8f0' }} title="Invoice photo" />
                          )}
                          {can('driver_reports','edit') && <button onClick={() => openStatusModal(inv)}
                            title="Change status (fix mistakes)"
                            style={{ padding: '4px 8px', background: '#eff6ff', border: 'none', borderRadius: 6, color: '#0284c7', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                            ✎
                          </button>}
                          {can('driver_reports','delete') && <button onClick={() => deleteInvoice(inv.id, s.id)}
                            style={{ padding: '4px 8px', background: '#fef2f2', border: 'none', borderRadius: 6, color: '#dc2626', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                            🗑
                          </button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!report.sessions.length && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', color: '#94a3b8', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              No sessions found for this driver in selected range
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, cursor: 'pointer', padding: 20 }}>
          <img src={lightbox} alt="full" style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: 12 }} />
        </div>
      )}

      {statusModal && (
        <div onClick={() => setStatusModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Change delivery status</h2>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{statusModal.customer_name} · {statusModal.customer_code}</div>

            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 8 }}>New status</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[['delivered','Delivered','#16a34a'],['cancelled','Cancelled','#dc2626'],['rescheduled','Rescheduled','#7c3aed'],['pending','Reset to pending','#64748b']].map(([val,label,col]) => (
                <button key={val} onClick={() => setNewStatus(val)} style={{
                  padding: '10px', borderRadius: 8, border: newStatus===val ? `2px solid ${col}` : '1px solid #e2e8f0',
                  background: newStatus===val ? col+'15' : '#fff', color: newStatus===val ? col : '#475569',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{label}</button>
              ))}
            </div>

            {(newStatus === 'cancelled' || newStatus === 'rescheduled') && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Reason (optional)</label>
                <input value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="e.g. driver marked wrong"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            )}

            {newStatus === 'rescheduled' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>New delivery date</label>
                <input type="date" value={statusDate} min={today} onChange={e => setStatusDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>A new pending delivery will be created for this date.</div>
              </div>
            )}

            {newStatus === 'pending' && (
              <div style={{ background: '#fef9c3', borderRadius: 8, padding: 10, fontSize: 12, color: '#854d0e', marginBottom: 14 }}>
                This resets the stop so the driver can deliver it again. All times and photos-status will be cleared.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setStatusModal(null)} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={submitStatusChange} style={{ padding: '10px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Update status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
